import { useState } from "react";
import SearchBar from "../../components/SearchBar";
import Pagination from "../../components/Pagination";
import ConfirmDialog from "../../components/ConfirmDialog";
import { useToast } from "../../context/ToastContext";
import {
  useAllPlaceImages,
  useDeleteAllPlaceImage,
} from "../../hooks/usePlaceImages";

const LIMIT = 100;

const FILTERS = [
  { key: "all", label: "All" },
  { key: "moderated", label: "Moderated" },
  { key: "non-moderated", label: "Non-moderated" },
];

// --- Field extraction (shared shape with the moderation queue) ---
const idOf = (img) => img.id ?? img.image_id ?? img.imageId;
const urlOf = (img) =>
  img.imageUrl ??
  img.image_url ??
  img.url ??
  img.image ??
  img.src ??
  img.path ??
  "";
const captionOf = (img) =>
  img.placeName ??
  img.place_name ??
  img.name ??
  img.title ??
  img.place?.name ??
  "";
const sourceOf = (img) => img.source ?? img.source_name ?? "";
const moderatedOf = (img) => img.moderated ?? img.is_moderated;

// filter -> query param (all: omit `moderated` entirely)
const moderatedParam = (filter) =>
  filter === "moderated" ? true : filter === "non-moderated" ? false : undefined;

export default function AllImagesPage() {
  const toast = useToast();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [deleting, setDeleting] = useState(null);

  const { data, isLoading, error } = useAllPlaceImages({
    page,
    limit: LIMIT,
    search,
    moderated: moderatedParam(filter),
  });

  // Accept both { data:[...] , total,... } envelope and a bare array.
  const rows = Array.isArray(data) ? data : data?.data || [];
  const total = Array.isArray(data) ? data.length : data?.total;

  const canDelete = filter === "moderated";

  const delM = useDeleteAllPlaceImage({
    onSuccess: () => {
      toast.success("Image deleted");
      setDeleting(null);
    },
    onError: (e) => toast.error(e.message || "Delete failed"),
  });

  // Group images by place name, preserving first-seen order.
  const groups = [];
  const groupIndex = new Map();
  for (const img of rows) {
    const label = captionOf(img) || "Unknown place";
    let g = groupIndex.get(label);
    if (!g) {
      g = { label, images: [] };
      groupIndex.set(label, g);
      groups.push(g);
    }
    g.images.push(img);
  }

  const changeFilter = (key) => {
    setFilter(key);
    setPage(1);
  };

  return (
    <div className="page">
      <div className="page__header">
        <h1>All Place Images</h1>
      </div>
      <div className="page__toolbar page__toolbar--row">
        <SearchBar
          value={search}
          onSearch={(v) => {
            setSearch(v);
            setPage(1);
          }}
          placeholder="Search images…"
        />
        <div className="seg">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              className={`seg__btn${filter === f.key ? " seg__btn--active" : ""}`}
              onClick={() => changeFilter(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="state state--loading">Loading…</div>
      ) : error ? (
        <div className="state state--error" role="alert">
          {error.message || "Failed to load images."}
        </div>
      ) : rows.length === 0 ? (
        <div className="state state--empty">No images found.</div>
      ) : (
        groups.map((group) => (
          <section className="img-group" key={group.label}>
            <h2 className="img-group__title">
              {group.label}
              <span className="img-group__count">{group.images.length}</span>
            </h2>
            <div className="img-grid">
              {group.images.map((img) => {
                const id = idOf(img);
                const url = urlOf(img);
                const source = sourceOf(img);
                const moderated = moderatedOf(img);
                return (
                  <div className="img-card" key={id ?? url}>
                    <div className="img-card__thumb">
                      {url ? (
                        <img
                          src={url}
                          alt={group.label || `image ${id ?? ""}`}
                          loading="lazy"
                        />
                      ) : (
                        <div className="img-card__missing">no image url</div>
                      )}
                      {moderated != null && (
                        <span
                          className={`img-card__flag${moderated ? " img-card__flag--on" : ""}`}
                        >
                          {moderated ? "Moderated" : "Not moderated"}
                        </span>
                      )}
                      {source && <span className="img-card__source">{source}</span>}
                    </div>
                    {canDelete && (
                      <div className="img-card__meta">
                        <button
                          className="btn btn--sm btn--danger"
                          onClick={() => setDeleting(img)}
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        ))
      )}

      {!Array.isArray(data) && data && total != null && (
        <Pagination page={page} limit={LIMIT} total={total} onPage={setPage} />
      )}

      <ConfirmDialog
        open={!!deleting}
        message="Delete this image? This cannot be undone."
        pending={delM.isPending}
        onCancel={() => setDeleting(null)}
        onConfirm={() => delM.mutate(idOf(deleting))}
      />
    </div>
  );
}
